import React, { useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Lightbulb, Plus, RefreshCw, Sparkles, TriangleAlert } from 'lucide-react';
import eventsApi from 'api/requests/events';
import Skeleton from 'components/_UI/Skeleton';
import StepSection from './StepSection';
import currency from 'helpers/currency';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

/**
 * Sugestões de produtos geradas por IA a partir do próprio evento.
 *
 * Busca sob demanda (nunca no mount): uma geração nova é uma chamada ao modelo
 * e custa alguns segundos, então o estado inicial é um convite com botão. Cada
 * cartão é só uma ideia — "Adicionar" pré-preenche um rascunho no formulário de
 * produto novo desta mesma etapa, onde o produtor ainda edita preço e estoque
 * antes de salvar. Nada é persistido por este componente.
 */

const STATUS = {
    IDLE: 'idle',
    LOADING: 'loading',
    READY: 'ready',
    ERROR: 'error',
    // Colaborador sem acesso (403) ou evento inexistente (404): a etapa segue
    // funcionando, só o painel de sugestões some — não é erro do produtor.
    HIDDEN: 'hidden',
};

// `product` fica de fora de propósito: um crachá "Produto" repetido em todo
// cartão não informa nada. Só o que foge do comum ganha rótulo.
const TYPE_LABELS = {
    sales_promotion: 'Combo',
    voucher: 'Voucher',
};

const GENERIC_ERROR = 'Não foi possível carregar as sugestões agora. Tente novamente em alguns instantes.';

function suggestionKey(suggestion) {
    return `${suggestion.type}:${suggestion.name}`.toLowerCase();
}

export default function ProductSuggestions({ eventSlug, existingNames = [], onAccept = () => false }) {
    const [status, setStatus] = useState(STATUS.IDLE);
    const [suggestions, setSuggestions] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [acceptedKeys, setAcceptedKeys] = useState([]);

    const takenNames = useMemo(
        () => new Set(existingNames.filter(Boolean).map(name => name.trim().toLowerCase())),
        [existingNames]
    );

    // O backend já filtra os produtos que o evento tem, mas os rascunhos criados
    // agora nesta tela ele não conhece — some com o cartão que virou rascunho.
    const visibleSuggestions = useMemo(
        () => suggestions.filter(item => (
            !acceptedKeys.includes(suggestionKey(item)) && !takenNames.has(item.name?.trim().toLowerCase())
        )),
        [suggestions, acceptedKeys, takenNames]
    );

    async function loadSuggestions(refresh = false) {
        if (!eventSlug) return;

        setStatus(STATUS.LOADING);
        setErrorMessage('');

        try {
            const { data } = await eventsApi.getProductSuggestions(eventSlug, { refresh });

            setSuggestions(data?.data || []);
            setAcceptedKeys([]);
            setStatus(STATUS.READY);
        } catch (error) {
            const httpStatus = error?.response?.status;

            if ([403, 404].includes(httpStatus)) {
                setStatus(STATUS.HIDDEN);
                return;
            }

            // 503/500 desta rota trazem mensagem já traduzida e segura de exibir.
            // Nos demais casos a mensagem é técnica — cai no texto genérico.
            const apiMessage = [500, 503].includes(httpStatus) ? error?.response?.data?.message : '';

            setErrorMessage(apiMessage || GENERIC_ERROR);
            setStatus(STATUS.ERROR);
        }
    }

    function handleAccept(suggestion) {
        setAcceptedKeys(prev => [...prev, suggestionKey(suggestion)]);
        onAccept(suggestion);
    }

    if (!eventSlug || status === STATUS.HIDDEN) {
        return null;
    }

    return (
        <StepSection
            id='product-suggestions'
            icon={Sparkles}
            title='Sugestões para este evento'
            hint='Geradas por inteligência artificial a partir do nome, da descrição e da data do seu evento — confira nome, preço e estoque antes de salvar. O que você adicionar sobe para "Criar do zero".'
            count={visibleSuggestions.length}
            badge={<AiBadge><Sparkles size={11} aria-hidden='true' />Gerado por IA</AiBadge>}
            action={status === STATUS.READY && (
                <GhostButton type='button' onClick={() => loadSuggestions(true)}>
                    <RefreshCw size={14} aria-hidden='true' />
                    Gerar novas sugestões
                </GhostButton>
            )}
        >
            <StatusRegion role='status' aria-live='polite'>
                {status === STATUS.IDLE && (
                    <InviteCard>
                        <InviteText>
                            <InviteTitle>Sem ideias do que vender?</InviteTitle>
                            <InviteHint>
                                A IA sugere produtos com nome, preço e estoque a partir do seu
                                evento — tudo editável antes de salvar.
                            </InviteHint>
                        </InviteText>
                        <PrimaryButton type='button' onClick={() => loadSuggestions(false)}>
                            <Sparkles size={16} aria-hidden='true' />
                            Sugerir produtos
                        </PrimaryButton>
                    </InviteCard>
                )}

                {status === STATUS.LOADING && (
                    <>
                        <VisuallyHidden>Gerando sugestões de produtos, isso pode levar alguns segundos.</VisuallyHidden>
                        <CardsGrid aria-hidden='true'>
                            {[0, 1, 2].map(index => (
                                <SkeletonCard key={index}>
                                    <Skeleton width='38%' height='14px' />
                                    <Skeleton width='75%' height='18px' style={{ marginTop: '12px' }} />
                                    <Skeleton width='100%' height='12px' style={{ marginTop: '10px' }} />
                                    <Skeleton width='85%' height='12px' style={{ marginTop: '6px' }} />
                                    <Skeleton width='60%' height='30px' style={{ marginTop: '18px' }} />
                                </SkeletonCard>
                            ))}
                        </CardsGrid>
                    </>
                )}

                {status === STATUS.ERROR && (
                    <FeedbackCard tone='danger'>
                        <FeedbackIcon tone='danger'>
                            <TriangleAlert size={18} aria-hidden='true' />
                        </FeedbackIcon>
                        <FeedbackText>
                            <FeedbackTitle>Não deu pra gerar sugestões</FeedbackTitle>
                            <FeedbackHint>{errorMessage}</FeedbackHint>
                        </FeedbackText>
                        <GhostButton type='button' onClick={() => loadSuggestions(false)}>
                            <RefreshCw size={14} aria-hidden='true' />
                            Tentar de novo
                        </GhostButton>
                    </FeedbackCard>
                )}

                {status === STATUS.READY && !visibleSuggestions.length && (
                    <FeedbackCard>
                        <FeedbackIcon>
                            <Lightbulb size={18} aria-hidden='true' />
                        </FeedbackIcon>
                        <FeedbackText>
                            <FeedbackTitle>
                                {suggestions.length ? 'Você já usou todas as sugestões' : 'Nenhuma sugestão por enquanto'}
                            </FeedbackTitle>
                            <FeedbackHint>
                                {suggestions.length
                                    ? 'Gere uma nova leva ou siga criando produtos do seu jeito abaixo.'
                                    : 'Descreva melhor o evento na primeira etapa ou tente gerar de novo.'}
                            </FeedbackHint>
                        </FeedbackText>
                        <GhostButton type='button' onClick={() => loadSuggestions(true)}>
                            <RefreshCw size={14} aria-hidden='true' />
                            Gerar de novo
                        </GhostButton>
                    </FeedbackCard>
                )}

                {status === STATUS.READY && !!visibleSuggestions.length && (
                    <>
                        <VisuallyHidden>
                            {visibleSuggestions.length} sugestões de produtos disponíveis.
                        </VisuallyHidden>

                        <CardsGrid>
                            {visibleSuggestions.map(item => (
                                <SuggestionCard key={suggestionKey(item)}>
                                    <CardHead>
                                        <Category>{item.category?.trim() || 'Outros'}</Category>
                                        {/* "Produto" em todo cartão seria ruído: o crachá só
                                            aparece quando o item não é um produto simples. */}
                                        {TYPE_LABELS[item.type] && (
                                            <TypeBadge>{TYPE_LABELS[item.type]}</TypeBadge>
                                        )}
                                    </CardHead>

                                    <CardName title={item.name}>{item.name}</CardName>

                                    {!!item.description && (
                                        <CardDescription>{item.description}</CardDescription>
                                    )}

                                    {!!item.reason && (
                                        <CardReason>
                                            <Lightbulb size={13} aria-hidden='true' />
                                            <span>{item.reason}</span>
                                        </CardReason>
                                    )}

                                    <CardNumbers>
                                        <NumberBlock>
                                            <NumberValue>{currency.format(item.suggested_price)}</NumberValue>
                                            <NumberLabel>preço sugerido</NumberLabel>
                                        </NumberBlock>
                                        <NumberBlock>
                                            <NumberValue>{item.suggested_quantity}</NumberValue>
                                            <NumberLabel>em estoque</NumberLabel>
                                        </NumberBlock>
                                    </CardNumbers>

                                    <AddButton
                                        type='button'
                                        onClick={() => handleAccept(item)}
                                        aria-label={`Adicionar ${item.name} ao evento`}
                                    >
                                        <Plus size={15} aria-hidden='true' />
                                        Adicionar
                                    </AddButton>
                                </SuggestionCard>
                            ))}
                        </CardsGrid>
                    </>
                )}
            </StatusRegion>
        </StepSection>
    );
}

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
`;

// Transparência: o produtor precisa saber que o conteúdo saiu de um modelo de
// IA — tanto pra revisar quanto pra decidir o quanto confia.
const AiBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 9px;
    border: 1px solid ${panelAccentAlpha(colors.occasionalPurple, '3D')};
    border-radius: 20px;
    color: ${panelAccent(colors.occasionalPurple)};
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
`;

const StatusRegion = styled.div``;

const InviteCard = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
    padding: 18px 20px;
    border: 2px dashed ${panelAccentAlpha(colors.occasionalPurple, '33')};
    border-radius: 14px;
    background: ${panelAccentAlpha(colors.occasionalPurple, '05')};

    @media screen and (min-width: ${breakpoints.sm}) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }
`;

const InviteText = styled.div`
    min-width: 0;
`;

const InviteTitle = styled.p`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const InviteHint = styled.p`
    font-size: 12px;
    color: ${colors.night};
    margin-top: 3px;
`;

const PrimaryButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    flex-shrink: 0;
    padding: 11px 18px;
    border: none;
    border-radius: 10px;
    background: ${panelAccent(colors.occasionalPurple)};
    color: ${colors.neutralLight};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
        background: ${panelAccent(colors.secondaryPurple)};
    }

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.occasionalPurple)};
        outline-offset: 2px;
    }
`;

const GhostButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    flex-shrink: 0;
    padding: 9px 14px;
    border: 1.5px solid ${panelAccentAlpha(colors.occasionalPurple, '44')};
    border-radius: 10px;
    background: transparent;
    color: ${panelAccent(colors.occasionalPurple)};
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        background: ${panelAccentAlpha(colors.occasionalPurple, '0D')};
        border-color: ${panelAccent(colors.occasionalPurple)};
    }

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.occasionalPurple)};
        outline-offset: 2px;
    }
`;

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;

    @media screen and (min-width: ${breakpoints.sm}) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        grid-template-columns: repeat(3, 1fr);
    }
`;

const SuggestionCard = styled.article`
    display: flex;
    flex-direction: column;
    padding: 16px;
    border: 1.5px dashed ${panelAccentAlpha(colors.occasionalPurple, '3D')};
    border-radius: 14px;
    background: ${colors.neutralLight};
    animation: ${fadeIn} 0.25s ease both;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover {
        border-color: ${panelAccentAlpha(colors.occasionalPurple, '80')};
        box-shadow: 0 2px 12px ${panelAccentAlpha(colors.occasionalPurple, '14')};
    }
`;

const CardHead = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
`;

// A categoria vem como texto livre do modelo ("Bebidas", "Combos"). Serve de
// etiqueta pra situar o item — não vira estrutura de navegação.
const Category = styled.span`
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: ${colors.night};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const TypeBadge = styled.span`
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    margin-top: 1px;
    padding: 3px 9px;
    border-radius: 20px;
    background: ${panelAccentAlpha(colors.occasionalPurple, '12')};
    color: ${panelAccent(colors.occasionalPurple)};
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
`;

const CardName = styled.h5`
    font-size: 14px;
    font-weight: 600;
    line-height: 1.3;
    color: ${colors.nightDark};
    overflow-wrap: anywhere;
`;

const CardDescription = styled.p`
    margin-top: 6px;
    font-size: 12px;
    line-height: 1.45;
    color: ${colors.night};
    overflow-wrap: anywhere;
`;

// Sem caixa cinza: o "porquê" é apoio, não um bloco concorrendo com a descrição.
const CardReason = styled.p`
    display: flex;
    align-items: flex-start;
    gap: 6px;
    margin-top: 10px;
    font-size: 11px;
    line-height: 1.45;
    color: ${colors.night};
    overflow-wrap: anywhere;

    & > svg {
        flex-shrink: 0;
        margin-top: 2px;
        color: ${panelAccent(colors.occasionalPurple)};
    }
`;

const CardNumbers = styled.div`
    display: flex;
    align-items: baseline;
    gap: 18px;
    margin-top: auto;
    padding-top: 14px;
`;

const NumberBlock = styled.div`
    min-width: 0;
`;

const NumberLabel = styled.span`
    display: block;
    margin-top: 1px;
    font-size: 11px;
    color: ${colors.night};
`;

const NumberValue = styled.span`
    display: block;
    margin-top: 2px;
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const AddButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    width: 100%;
    margin-top: 12px;
    padding: 10px 14px;
    border: 1.5px solid ${panelAccent(colors.occasionalPurple)};
    border-radius: 10px;
    background: transparent;
    color: ${panelAccent(colors.occasionalPurple)};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        background: ${panelAccent(colors.occasionalPurple)};
        color: ${colors.neutralLight};
    }

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.occasionalPurple)};
        outline-offset: 2px;
    }
`;

const SkeletonCard = styled.div`
    padding: 16px;
    border: 1.5px dashed ${colors.night}1A;
    border-radius: 12px;
    background: ${colors.neutralLight};
`;

const FeedbackCard = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 16px 18px;
    border: 1.5px solid ${({ tone }) => tone === 'danger' ? colors.danger + '3D' : colors.night + '1F'};
    border-radius: 12px;
    background: ${({ tone }) => tone === 'danger' ? colors.danger + '08' : colors.night + '05'};

    @media screen and (min-width: ${breakpoints.sm}) {
        flex-direction: row;
        align-items: center;
    }
`;

const FeedbackIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    flex-shrink: 0;
    background: ${({ tone }) => tone === 'danger' ? colors.danger + '14' : panelAccentAlpha(colors.occasionalPurple, '12')};
    color: ${({ tone }) => tone === 'danger' ? colors.dangerDark : panelAccent(colors.occasionalPurple)};
`;

const FeedbackText = styled.div`
    flex: 1;
    min-width: 0;
`;

const FeedbackTitle = styled.p`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const FeedbackHint = styled.p`
    font-size: 12px;
    line-height: 1.45;
    color: ${colors.night};
    margin-top: 3px;
`;

const VisuallyHidden = styled.span`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
`;
